// Round-trip URL search params <-> typed filter object for the Pricing
// Type management page. Non-throwing per-field coercers so a malformed
// URL (e.g. `?page=banana` or `?page=0`) silently falls back to defaults
// instead of handing the user TanStack Router's unbranded error screen.
// Mirrors the `validateProductsSearch` convention.

export type PricingTypeSortField = 'title' | 'type';
export type PricingTypeSortOrder = 'asc' | 'desc';

export type PricingTypeSearch = {
  page?: number;
  limit?: number;
  sort?: PricingTypeSortField;
  order?: PricingTypeSortOrder;
};

const asPositiveInt = (v: unknown): number | undefined => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 ? n : undefined;
};

const asLimit = (v: unknown): number | undefined => {
  const n = asPositiveInt(v);
  return n !== undefined && n <= 200 ? n : undefined;
};

const asSort = (v: unknown): PricingTypeSortField | undefined =>
  v === 'title' || v === 'type' ? v : undefined;

const asOrder = (v: unknown): PricingTypeSortOrder | undefined =>
  v === 'asc' || v === 'desc' ? v : undefined;

export const validatePricingTypeSearch = (
  input: Record<string, unknown>
): PricingTypeSearch => ({
  page: asPositiveInt(input.page),
  limit: asLimit(input.limit),
  sort: asSort(input.sort),
  order: asOrder(input.order),
});

// Generic siblings-preserving merger so a future filter doesn't clobber
// pagination, mirroring the products page's helper.
export const mergeSearch = (
  prev: Record<string, unknown>,
  patch: Partial<PricingTypeSearch>
): Record<string, unknown> => ({ ...prev, ...patch });
